import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb } from "../components/shared/Breadcrumb";
import { DesignCard } from "../components/shared/DesignCard";
import { PageMeta } from "../components/shared/PageMeta";
import { ButtonLink } from "../components/shared/Button";
import { designs, frontageGuides } from "../data/designs";

// Catalogue → Bricks query loop on the "design" CPT with native filters (frontage, bedrooms).
// Bathrooms, car spaces and storeys are identical across all records, so those filters are hidden.
type FrontageFilter = "all" | "10m" | "15m";
type BedroomFilter = "any" | "3" | "4";
const frontageOptions: {
  value: FrontageFilter;
  label: string;
}[] = [{
  value: "all",
  label: "All"
}, {
  value: "10m",
  label: "10m"
}, {
  value: "15m",
  label: "15m"
}];
const bedroomOptions: {
  value: BedroomFilter;
  label: string;
}[] = [{
  value: "any",
  label: "Any"
}, {
  value: "3",
  label: "3 bed"
}, {
  value: "4",
  label: "4 bed"
}];
export function HomeDesigns() {
  const [frontage, setFrontage] = useState<FrontageFilter>("all");
  const [bedrooms, setBedrooms] = useState<BedroomFilter>("any");
  const results = useMemo(() => designs.filter(design => (frontage === "all" || design.frontage === frontage) && (bedrooms === "any" || String(design.bedrooms) === bedrooms)), [frontage, bedrooms]);
  const filtered = frontage !== "all" || bedrooms !== "any";
  const reset = () => {
    setFrontage("all");
    setBedrooms("any");
  };
  const segment = (selected: boolean) => `min-h-[44px] whitespace-nowrap px-5 text-[15px] font-semibold transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean ${selected ? "bg-charcoal text-paper" : "bg-white text-charcoal hover:bg-paper"}`;
  return <>
      <PageMeta title="Single-Storey Home Designs Perth | Oceanside Living" description="Compare Oceanside Living’s single-storey home designs for 10m and 15m frontage categories, with bedrooms, living area and total under roof." data-id="element-696" data-mp="2j26mtvptm813" />
      <div className="mx-auto max-w-site px-5 pb-16 pt-10 md:px-10 md:pb-28 md:pt-14" data-id="element-697" data-mp="jug6a4aaheaq">
        <Breadcrumb items={[{
        label: "Home",
        to: "/"
      }, {
        label: "Home designs"
      }]} data-id="element-698" data-mp="ex6i0rymz8oh" />
        <h1 className="mt-6 text-[38px] font-semibold leading-[1.08] tracking-tight md:text-[60px]" data-id="element-699" data-mp="avckutnngmt2">Home designs</h1>
        <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-slate md:text-lg" data-id="element-700" data-mp="17jiw3zf2udgb">
          Oceanside Living publishes four single-storey designs for 10m and 15m frontage categories. Filter by frontage and
          bedrooms, then talk with us about how a design suits your block.
        </p>

        <div className="mt-10 flex flex-col gap-6 border-y border-line py-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-701" data-mp="tk9ecvb83ba8">
          <div className="flex flex-col gap-5 sm:flex-row sm:gap-10" data-id="element-702" data-mp="3j8a31j2817l5">
            <fieldset data-id="element-703" data-mp="3eagz6woe4po1">
              <legend className="mb-2 text-sm font-semibold" data-id="element-704" data-mp="3e9ytl9fu8z3g">Frontage category</legend>
              <div className="inline-flex divide-x divide-line overflow-hidden rounded-full border border-line" data-id="element-705" data-mp="1tusor71ivl65">
                {frontageOptions.map(option => <button key={option.value} type="button" aria-pressed={frontage === option.value} onClick={() => setFrontage(option.value)} className={segment(frontage === option.value)} data-id="element-706" data-mp="22tnrua99sf58">
                    {option.label}
                  </button>)}
              </div>
            </fieldset>
            <fieldset data-id="element-707" data-mp="2hhr1btmiu8ys">
              <legend className="mb-2 text-sm font-semibold" data-id="element-708" data-mp="2hgz3ec5gbz5c">Bedrooms</legend>
              <div className="inline-flex divide-x divide-line overflow-hidden rounded-full border border-line" data-id="element-709" data-mp="2miy5lipggq0p">
                {bedroomOptions.map(option => <button key={option.value} type="button" aria-pressed={bedrooms === option.value} onClick={() => setBedrooms(option.value)} className={segment(bedrooms === option.value)} data-id="element-710" data-mp="s45enwzionf3">
                    {option.label}
                  </button>)}
              </div>
            </fieldset>
          </div>
          <div className="flex items-center gap-5" data-id="element-711" data-mp="3sy1ruoqxdqbd">
            <p role="status" aria-live="polite" className="text-[15px] text-slate" data-id="element-712" data-mp="3t1frhp87flf4">
              Showing <span className="font-semibold text-charcoal" data-id="element-713" data-mp="ar4827ga6ifb">{results.length}</span> of {designs.length} designs
            </p>
            {filtered && <button type="button" onClick={reset} className="text-[15px] font-medium underline underline-offset-4" data-id="element-714" data-mp="3eb8kc5nndotq">
                Reset filters
              </button>}
          </div>
        </div>

        {results.length > 0 ? <div className="mt-10 grid gap-6 md:grid-cols-2" data-id="element-715" data-mp="3o1tixt3bmtcn">
            {results.map(design => <DesignCard key={design.slug} design={design} headingLevel="h2" data-id="element-716" data-mp="3j7gybfq3zbzb" />)}
          </div> : <div className="mt-10 rounded-[28px] border border-line bg-white p-8 text-center md:p-12" data-id="element-717" data-mp="2ql4hi0ia9t3s">
            <h2 className="text-2xl font-semibold" data-id="element-718" data-mp="16221yuvda1ar">No designs match those filters</h2>
            <p className="mx-auto mt-3 max-w-xl text-[17px] text-slate" data-id="element-719" data-mp="1at0m65jh7jja">
              Try a different combination, or tell us about your block and what you need. A custom home may suit you better.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3" data-id="element-720" data-mp="112z9e9wkpnjo">
              <button type="button" onClick={reset} className="inline-flex min-h-[52px] items-center rounded-full border-[1.5px] border-charcoal px-6 text-[15px] font-semibold" data-id="element-721" data-mp="1ot1ych56yde2">
                Reset filters
              </button>
              <ButtonLink to="/contact?project=custom" data-id="element-722" data-mp="1ft6ihz0oxu7p">Discuss a custom home</ButtonLink>
            </div>
          </div>}

        <div className="mt-16 grid gap-6 md:grid-cols-2" data-id="element-723" data-mp="1kpwy6l8l2nsl">
          {frontageGuides.map(guide => <section key={guide.frontage} aria-labelledby={`guide-${guide.frontage}`} className="rounded-[28px] border border-line bg-white p-7 md:p-10" data-id="element-724" data-mp="4bnbdvjzp73v">
              <span className="rounded-full bg-charcoal px-3 py-1 text-[13px] font-semibold text-paper" data-id="element-725" data-mp="2b209o7tm6k4g">{guide.frontage} frontage</span>
              <h2 id={`guide-${guide.frontage}`} className="mt-5 text-[24px] font-semibold tracking-tight md:text-[28px]" data-id="element-726" data-mp="262xi9q6k4cey">{guide.title}</h2>
              <p className="mt-3 text-[16px] leading-relaxed text-slate" data-id="element-727" data-mp="1xa2aaoppg2lh">{guide.intro}</p>
              <dl className="mt-6 space-y-4 border-t border-line pt-6" data-id="element-728" data-mp="1x89bfvlc2mo1">
                {guide.points.map(point => <div key={point.title} data-id="element-729" data-mp="1nc5yrvsuv5mu">
                    <dt className="font-semibold" data-id="element-730" data-mp="1nabduhhl3dir">{point.title}</dt>
                    <dd className="mt-1 text-[15px] leading-relaxed text-slate" data-id="element-731" data-mp="1ic1hi3k4i6vk">{point.text}</dd>
                  </div>)}
              </dl>
            </section>)}
        </div>

        <div className="mt-16 grid gap-8 border-t border-charcoal pt-10 lg:grid-cols-12" data-id="element-732" data-mp="3cr451vpm3evu">
          <div className="lg:col-span-7" data-id="element-733" data-mp="38opmzdtroj5m">
            <h2 className="text-[26px] font-semibold tracking-tight md:text-[32px]" data-id="element-734" data-mp="38q0o15vtvct1">Every design can be tailored</h2>
            <p className="mt-4 text-[17px] leading-relaxed text-slate" data-id="element-735" data-mp="ufdi4k4devb0">
              Customisation options are available throughout the design process. A frontage category is a starting point, not a
              guarantee: depth, orientation, setbacks, slope, easements and local planning rules all matter, and we review those with you.
            </p>
          </div>
          <div className="flex flex-col gap-4 lg:col-span-5 lg:items-end lg:justify-end" data-id="element-736" data-mp="bjx5qjdkm42a">
            <ButtonLink to="/contact?project=unsure" data-id="element-737" data-mp="gihyjxqhtlgp">Talk to us about your block</ButtonLink>
            <Link to="/#services" className="text-[15px] font-medium underline underline-offset-4" data-id="element-738" data-mp="ki8v6i2pavt5">See everything we build</Link>
          </div>
        </div>
      </div>
    </>;
}